import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import BranchForm from '../../components/Form/BranchForm';
import { Branch, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useBranches, useCreateBranch, useUpdateBranch, useDeleteBranch } from '../../hooks/useBranches';
import { useEmployees } from '../../hooks/useEmployees';
import TableSkeleton from '../../components/Loading/TableSkeleton';

const Branches: React.FC = () => {
  // Fetch employees from API - NO MOCK DATA
  const { data: employees = [] } = useEmployees({ populate: '*' });
  // Query options for fetching branches
  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'name', order: 'asc' as const }]
  }), []);

  // Fetch branches using the API hook
  const { data: branches, isLoading, error, refetch } = useBranches(queryOptions);

  // Create, update, and delete mutations
  const createMutation = useCreateBranch(() => {
    refetch();
  });

  const updateMutation = useUpdateBranch(() => {
    refetch();
  });

  const deleteMutation = useDeleteBranch(() => {
    refetch();
  });

  const [showForm, setShowForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | undefined>();

  // Get employee name from API data - NO MOCK DATA
  const getEmployeeName = useCallback((employeeId: string): string => {
    if (!employees || employees.length === 0) return 'Loading...';
    const employee = employees.find((emp: any) => 
      emp.documentId === employeeId || emp.id?.toString() === employeeId
    );
    return employee ? `${employee.name || 'Unknown'} (${employee.rank || employee.position || 'Employee'})` : 'Not Found';
  }, [employees]);

  // Contacts with fallback to legacy single fields
  const getContacts = useCallback((branch: Branch) => {
    if (branch.contacts && branch.contacts.length > 0) return branch.contacts;
    const legacy = [];
    const firstManager = branch.type === 'vessel' ? branch.vesselManager1 : branch.manager;
    if (firstManager || branch.phone || branch.email) {
      legacy.push({ manager: firstManager, phone: branch.phone, email: branch.email });
    }
    if (branch.type === 'vessel' && branch.vesselManager2) {
      legacy.push({ manager: branch.vesselManager2, phone: '', email: '' });
    }
    return legacy;
  }, []);

  const renderContactField = useCallback((branch: Branch, field: 'manager' | 'phone' | 'email') => {
    const values = getContacts(branch)
      .map((c) => (field === 'manager' ? (c.manager ? getEmployeeName(c.manager) : '') : c[field]))
      .filter(Boolean) as string[];
    if (values.length === 0) return field === 'manager' ? 'Not assigned' : '-';
    return (
      <div className="text-sm space-y-0.5">
        {values.map((value, i) => <div key={i}>{value}</div>)}
      </div>
    );
  }, [getContacts, getEmployeeName]);

  const columns: TableColumn[] = [
    { key: 'name', label: 'Vessel/Headquarters Name', sortable: true },
    { key: 'type', label: 'Type', sortable: true, render: (value: string) => value.charAt(0).toUpperCase() + value.slice(1) },
    { key: 'location', label: 'Location', sortable: true },
    { key: 'manager', label: 'Manager(s)', sortable: true, render: (_: string, item: Branch) => renderContactField(item, 'manager') },
    { key: 'amount', label: 'Amount', sortable: true, render: (value: number | null) => `$${(value ?? 0).toLocaleString()}` },
    { key: 'phone', label: 'Phone', sortable: false, render: (_: string, item: Branch) => renderContactField(item, 'phone') },
    { key: 'email', label: 'Email', sortable: false, render: (_: string, item: Branch) => renderContactField(item, 'email') },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{value}</span>
    ) },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() }
  ];

  const buildExportData = () =>
    (branches || []).map(branch => {
      const contacts = getContacts(branch);
      return {
        'Name': branch.name,
        'Type': branch.type.charAt(0).toUpperCase() + branch.type.slice(1),
        'Location': branch.location,
        'Manager': contacts.map(c => (c.manager ? getEmployeeName(c.manager) : '')).filter(Boolean).join(' | '),
        'Amount': branch.amount,
        'Phone': contacts.map(c => c.phone).filter(Boolean).join(' | '),
        'Email': contacts.map(c => c.email).filter(Boolean).join(' | '),
        'Status': branch.status,
        'Created': new Date(branch.createdAt).toLocaleDateString()
      };
    });

  const handleExport = () => {
    if (!branches) return;
    exportToExcel(buildExportData(), `vessels_headquarters_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    if (!branches) return;
    printAsExcel(buildExportData(), 'Vessels and Headquarters');
  };

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setShowForm(true);
  };

  const handleDelete = async (branch: Branch) => {
    if (window.confirm(`Are you sure you want to delete ${branch.name}?`)) {
      await deleteMutation.execute(branch);
    }
  };

  const handleAdd = () => {
    setEditingBranch(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Branch>) => {
    if (editingBranch) {
      // Update existing branch - include documentId in data
      const updateData = {
        ...data,
        documentId: editingBranch.documentId
      };
      await updateMutation.execute({ id: editingBranch.id, data: updateData });
    } else {
      // Add new branch
      await createMutation.execute(data);
    }
    setShowForm(false);
    setEditingBranch(undefined);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBranch(undefined);
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <BranchForm
          branch={editingBranch}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Vessels and Headquarters</h1>
        <p className="text-gray-600">Manage your vessels, headquarters, and other locations</p>
      </div>
      <div className="flex justify-end space-x-3 mb-4">
        <button 
          onClick={handleExport}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Export
        </button>
        <button 
          onClick={handlePrint}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Print
        </button>
      </div>
      {isLoading ? (
        <TableSkeleton />
      ) : error ? (
        <div className="card p-6 text-center text-red-600">
          Error loading branches: {error.message || String(error)}
        </div>
      ) : (
      <Table
        columns={columns}
          data={branches || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title="Vessels and Headquarters Directory"
        searchPlaceholder="Search vessels and headquarters..."
      />
      )}
    </div>
  );
};

export default Branches; 